// Pestaña Pagos de Mis comisiones: el saldo (depositado, pendiente con su motivo o lo que debe, y lo
// retenido para el día 1) y el cronograma de pagos del mock: los depósitos de los últimos 14 días y
// lo que viene, proyectado. Mockup: surekeep/mockups/backoffice/portal-vendedor-comisiones.html
import type { ReactNode } from 'react';
import type { RetailCommission } from '@fiado/type-kit';
import { Badge, ErrorState, Loading } from '@/kit';
import { ApiError, userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import { formatDateMx, formatShortDayMx, formatTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { useMyPayments } from '../api/useMyPayments';
import { balanceViewOf, type BalanceView, type PaymentTone } from '../lib/myPaymentsView';
import {
    cutHeadOf,
    debtCutTextOf,
    monthlyCardOf,
    nextCutOf,
    paymentScheduleOf,
    pendingCardOf,
    retainedReleaseDay,
    SCHEDULE_DAYS,
    type MonthlyCard,
    type PendingCard,
    type ScheduleLane,
    type ScheduleRow,
} from '../lib/paymentSchedule';
import { CARD, TITLE } from './cardStyles';

type UpcomingCut = RetailCommission.MyCommissionsUpcomingCut;

const TONE_CARD: Record<PaymentTone, string> = {
    success: 'border-success-soft-border bg-success-soft',
    info: 'border-info-soft-border bg-info-soft',
    warning: 'border-warning-soft-border bg-warning-soft',
    danger: 'border-danger-soft-border bg-danger-soft',
};
const TONE_TEXT: Record<PaymentTone, string> = {
    success: 'text-success',
    info: 'text-info',
    warning: 'text-warning',
    danger: 'text-danger',
};
const TONE_DOT: Record<PaymentTone, string> = {
    success: 'bg-success',
    info: 'bg-info',
    warning: 'bg-warning',
    danger: 'bg-danger',
};
const TONE_RING: Record<PaymentTone, string> = {
    success: 'border-success',
    info: 'border-info',
    warning: 'border-warning',
    danger: 'border-danger',
};
/** Un depósito se pinta por su estado; lo proyectado, por el color de su carril. */
const LANE_TONE: Record<ScheduleLane, PaymentTone> = {
    deposit: 'success',
    payable: 'success',
    cut: 'info',
    monthly: 'warning',
};

/** Lo que entra lleva "+"; un depósito que no se hizo, no. */
const signedMoney = (cents: number, signed: boolean) =>
    `${signed && cents > 0 ? '+' : ''}${formatMoneyCents(cents)}`;

function BalanceCard({
    tone,
    tag,
    label,
    amount,
    children,
}: {
    tone: PaymentTone;
    tag: string;
    label: string;
    amount: string;
    children?: ReactNode;
}) {
    return (
        <li className={cn('flex flex-col gap-1 rounded-xl border px-4 py-3.5', TONE_CARD[tone])}>
            <span
                className={cn(
                    'self-start rounded px-[7px] py-[3px] text-[9px] font-extrabold uppercase tracking-[0.06em] text-white',
                    TONE_DOT[tone],
                )}
            >
                {tag}
            </span>
            <p
                className={cn(
                    'mt-1 text-[10.5px] font-bold uppercase tracking-[0.05em]',
                    TONE_TEXT[tone],
                )}
            >
                {label}
            </p>
            <p
                className={cn(
                    'text-[24px] font-extrabold leading-[1.1] tracking-[-0.02em] tabular-nums',
                    TONE_TEXT[tone],
                )}
            >
                {amount}
            </p>
            {children && <div className="text-[11px] text-foreground">{children}</div>}
        </li>
    );
}

/** Lo proyectado no es saldo: va aparte, con su monto en negrita y "proyectado". */
function ProjectedLine({
    tone,
    lead,
    amount,
    tail,
}: {
    tone: PaymentTone;
    lead: string;
    amount: string;
    tail: string;
}) {
    return (
        <p className={TONE_TEXT[tone]}>
            {lead}
            <b>{amount}</b>
            {tail}
        </p>
    );
}

/** Lo que un cierre abierto va a mover; si descuenta, va en negativo. */
function OpenCutLine({ cut, today }: { cut: UpcomingCut; today: string }) {
    const discount = cut.payableCents < 0;
    return (
        <ProjectedLine
            tone={discount ? 'danger' : 'info'}
            lead={`${cutHeadOf(cut, today)}: `}
            amount={signedMoney(cut.payableCents, true)}
            tail={discount ? ' · se descuenta al cerrar' : ' · se fija al cerrar'}
        />
    );
}

/** Con deuda, lo que genera el cierre se aplica primero a ella. */
function DebtCutLine({
    cut,
    debtCents,
    today,
}: {
    cut: UpcomingCut;
    debtCents: number;
    today: string;
}) {
    const { lead, amount, tail } = debtCutTextOf(cut, debtCents, today);
    return (
        <p className="text-foreground">
            {lead}
            <b>{amount}</b>
            {tail}
        </p>
    );
}

function BalanceCards({
    balance,
    today,
    nextCut,
    pending,
    monthly,
}: {
    balance: BalanceView;
    today: string;
    nextCut: UpcomingCut | null;
    pending: PendingCard;
    monthly: MonthlyCard;
}) {
    const monthlyDay =
        monthly.days.length > 1
            ? null
            : (monthly.days[0]?.day ?? retainedReleaseDay(today, balance.retainedCents));
    return (
        <ul className="grid gap-3 md:grid-cols-3" aria-label="Tu saldo">
            <BalanceCard
                tone="success"
                tag="Depositado"
                label="Ya cayó a tu wallet"
                amount={formatMoneyCents(balance.depositedCents)}
            >
                {balance.paidOutsideCents > 0 && (
                    <p>Tu cadena te pagó por fuera {formatMoneyCents(balance.paidOutsideCents)}</p>
                )}
            </BalanceCard>
            {balance.debtCents > 0 ? (
                <BalanceCard
                    tone="danger"
                    tag="Deuda"
                    label="Por descontar"
                    amount={`Debes ${formatMoneyCents(balance.debtCents)}`}
                >
                    {balance.inTransitCents > 0 && (
                        <p>En camino {formatMoneyCents(balance.inTransitCents)}</p>
                    )}
                    {nextCut && (
                        <DebtCutLine cut={nextCut} debtCents={balance.debtCents} today={today} />
                    )}
                    <p>Se descuenta de tus siguientes comisiones.</p>
                    {balance.since && <p>Desde el {formatDateMx(balance.since)}</p>}
                </BalanceCard>
            ) : (
                <BalanceCard
                    tone="info"
                    tag="Pendiente"
                    label="Por depositar"
                    amount={formatMoneyCents(pending.totalCents)}
                >
                    {pending.closedCents > 0 && pending.openCuts.length > 0 && (
                        <p>De cortes cerrados {formatMoneyCents(pending.closedCents)}</p>
                    )}
                    {pending.inTransitCents > 0 && (
                        <p>En camino {formatMoneyCents(pending.inTransitCents)}</p>
                    )}
                    {balance.reason && (
                        <p className="font-semibold text-warning">
                            {balance.reason}
                            {balance.since && ` · desde el ${formatDateMx(balance.since)}`}
                        </p>
                    )}
                    {[...pending.openCuts, ...pending.discounts].map((cut) => (
                        <OpenCutLine key={cut.periodId} cut={cut} today={today} />
                    ))}
                    {pending.totalCents === 0 && pending.discounts.length === 0 && (
                        <p>Nada pendiente.</p>
                    )}
                </BalanceCard>
            )}
            <BalanceCard
                tone="warning"
                tag="Mensual"
                label={
                    monthlyDay
                        ? `Se paga el día 1 · ${formatShortDayMx(monthlyDay)}`
                        : 'Se paga el día 1'
                }
                amount={formatMoneyCents(monthly.totalCents)}
            >
                <p>Lo de las reglas que se pagan al cierre del mes.</p>
                {monthly.openCents > 0 && monthly.closedCents > 0 && (
                    <p>De cortes cerrados {formatMoneyCents(monthly.closedCents)}</p>
                )}
                {monthly.openCents > 0 && (
                    <ProjectedLine
                        tone="warning"
                        lead="De semanas abiertas: "
                        amount={`+${formatMoneyCents(monthly.openCents)}`}
                        tail=" · se fija al cerrar"
                    />
                )}
                {monthly.days.length > 1 &&
                    monthly.days.map(({ day, cents }) => (
                        <p key={day}>
                            {formatShortDayMx(day)}: {formatMoneyCents(cents)}
                        </p>
                    ))}
            </BalanceCard>
        </ul>
    );
}

function ScheduleItem({ row }: { row: ScheduleRow }) {
    // Un cierre que descuenta va en rojo.
    const tone = row.status?.tone ?? (row.amountCents < 0 ? 'danger' : LANE_TONE[row.lane]);
    return (
        <li className="grid grid-cols-[52px_10px_1fr_auto_auto] items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-muted/20 sm:grid-cols-[84px_10px_1fr_auto_auto]">
            {/* En pantalla angosta la hora va abajo del día. */}
            <span className="flex flex-col text-[10px] leading-tight tabular-nums text-muted-foreground sm:block">
                <b className="text-[10.5px] text-foreground">{formatShortDayMx(row.day)}</b>
                {row.at && ` ${formatTimeMx(row.at)}`}
            </span>
            {/* Lleno, un depósito; hueco, lo proyectado. */}
            <span
                aria-hidden
                className={cn(
                    'size-[9px] justify-self-center rounded-full',
                    row.status ? TONE_DOT[tone] : cn('border-2 bg-card', TONE_RING[tone]),
                )}
            />
            <span className="flex min-w-0 flex-wrap items-baseline gap-1.5">
                <b className="sm:truncate">{row.title}</b>
                <small className="truncate text-[9.5px] text-muted-foreground">{row.detail}</small>
            </span>
            {row.status ? (
                <Badge variant={row.status.tone} size="xs">
                    {row.status.label}
                </Badge>
            ) : (
                <Badge variant="muted" size="xs">
                    Proyectado
                </Badge>
            )}
            <b
                className={cn(
                    'min-w-[70px] text-right font-extrabold tabular-nums',
                    TONE_TEXT[tone],
                )}
            >
                {signedMoney(row.amountCents, !row.status?.failed)}
            </b>
        </li>
    );
}

function ScheduleList({ label, rows }: { label: string; rows: readonly ScheduleRow[] }) {
    return (
        <ol aria-label={label} className="flex flex-col divide-y divide-border/50">
            {rows.map((row) => (
                <ScheduleItem key={row.key} row={row} />
            ))}
        </ol>
    );
}

const LEGEND: ReadonlyArray<{ label: string; dot: string }> = [
    { label: 'Depositado en tu wallet', dot: 'bg-success' },
    { label: 'Por depositar', dot: 'border-2 border-success bg-card' },
    { label: 'Cierre de semana (lunes)', dot: 'border-2 border-info bg-card' },
    { label: 'Cierre de mes (día 1)', dot: 'border-2 border-warning bg-card' },
];

export function MyPaymentsPanel({
    mine,
    today,
}: {
    mine: RetailCommission.GetMyCommissionsResponse;
    /** Hoy en el piso de venta, `YYYY-MM-DD`. */
    today: string;
}) {
    const query = useMyPayments();
    if (query.isPending) return <Loading text="Cargando tus pagos" />;
    if (!query.data) {
        // Con un 403, reintentar no cambia nada.
        const forbidden = query.error instanceof ApiError && query.error.httpStatus === 403;
        return (
            <ErrorState
                title="No se pudieron cargar tus pagos"
                description={userMessageOf(query.error)}
                onRetry={
                    forbidden
                        ? undefined
                        : () => {
                              void query.refetch();
                          }
                }
            />
        );
    }

    const balance = balanceViewOf(query.data.balance);
    const schedule = paymentScheduleOf(mine, balance, query.data.payments, today);
    const empty = schedule.upcoming.length === 0 && schedule.recent.length === 0;

    return (
        <>
            {/* Un refetch fallido no tapa lo que ya se leyó. */}
            {query.isError && (
                <p role="status" className="text-[11px] text-muted-foreground">
                    No se pudieron actualizar tus pagos. Lo que ves es la última lectura.
                </p>
            )}
            <BalanceCards
                balance={balance}
                today={today}
                nextCut={nextCutOf(mine)}
                pending={pendingCardOf(mine, balance)}
                monthly={monthlyCardOf(mine, balance, today)}
            />

            <section aria-label="Cronograma de pagos" className={CARD}>
                <h2 className={TITLE}>Cronograma de pagos</h2>
                <p className="mb-2 text-[10.5px] text-muted-foreground">
                    Últimos {SCHEDULE_DAYS} días · pendientes proyectados
                </p>
                <ul className="mb-2 flex flex-wrap gap-3 border-b border-dashed border-border pb-2 text-[10.5px] text-muted-foreground">
                    {LEGEND.map((item) => (
                        <li key={item.label} className="inline-flex items-center gap-1">
                            <span
                                aria-hidden
                                className={cn('inline-block size-2.5 rounded-full', item.dot)}
                            />
                            {item.label}
                        </li>
                    ))}
                </ul>
                {empty ? (
                    <p className="text-[11px] text-muted-foreground">
                        Sin depósitos en los últimos {SCHEDULE_DAYS} días ni pagos por venir.
                    </p>
                ) : (
                    <>
                        {schedule.upcoming.length > 0 && (
                            <ScheduleList label="Próximos pagos" rows={schedule.upcoming} />
                        )}
                        <div
                            role="separator"
                            aria-label={`Hoy · ${formatShortDayMx(today)}`}
                            className="flex items-center gap-2 py-1 text-[9.5px] font-extrabold uppercase tracking-[0.08em] text-primary"
                        >
                            <span
                                aria-hidden
                                className="h-0.5 flex-1 border-t-2 border-dashed border-primary"
                            />
                            Hoy · {formatShortDayMx(today)}
                            <span
                                aria-hidden
                                className="h-0.5 flex-1 border-t-2 border-dashed border-primary"
                            />
                        </div>
                        {schedule.recent.length > 0 && (
                            <ScheduleList label="Depósitos recientes" rows={schedule.recent} />
                        )}
                    </>
                )}
            </section>

            {schedule.older.length > 0 && (
                <section aria-label="Depósitos anteriores" className={CARD}>
                    <h2 className={TITLE}>Depósitos anteriores</h2>
                    <ScheduleList label="Depósitos anteriores" rows={schedule.older} />
                </section>
            )}
        </>
    );
}
